import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,  
  Typography, 
  CircularProgress, 
  Tooltip, 
} from '@mui/material';
import { 
  AddCircleOutline, 
  Delete,
  Subscriptions,
  Unsubscribe,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};


const Notifications = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [vapidPublicKey, setVapidPublicKey] = useState(null);
    const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState(null);
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null);
    const [idToDelete, setIdToDelete] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


    const fetchNotifications = useCallback(async () => {
        try {
            const response = await api.get('/notifications/notificationsofuser');
            setNotifications(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch notifications!');
        }
    }, []);

    const fetchVapidPublicKey = async () => {
        try {
            const response = await api.get('/notifications/vapidPublicKey');
            setVapidPublicKey(response.data.data.publicKey);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch VAPID public key!');
        }
    };

    const checkSubscription = async () => {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            setError('Service Worker not supported!');
            return;
        }
        try {
            const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
            setServiceWorkerRegistration(registration);
            const subscription = await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
        } catch (err) {
            setError(`Failed to register service worker: ${err.message}`);
        }
    };

    useEffect(() => {
        fetchNotifications();
        fetchVapidPublicKey();
        checkSubscription();
    }, [fetchNotifications]);

    const handleSubscribe = async () => {
        try {
            setLoading(true);

            if (!('Notification' in window)) {
                toast.error('Push notifications are not supported on this device.');
                return;
            }

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                toast.error('Notification permission denied. Please allow notifications in your browser/device settings.');
                return;
            }

            const registration = serviceWorkerRegistration || (await navigator.serviceWorker.ready);
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });
            await api.post('/notifications/subscribe', subscription);
            setIsSubscribed(true);
            toast.success('Subscribed to notifications.');
        } catch (err) {
            toast.error(err.response?.data?.message || `Subscription failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUnsubscribe = async () => {
        try {
            setLoading(true);
            const registration = serviceWorkerRegistration || (await navigator.serviceWorker.ready);
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
                await api.delete('/notifications/unsubscribe');
                setIsSubscribed(false);
                toast.success('Unsubscribed from notifications.');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || `Unsubscription failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            filterable: false,
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            ...(isMobile ? { width: 60 } : { flex: 1 }),
        },
        {
            field: "type",
            headerName: "Type",
            ...(isMobile ? { width: 100 } : { flex: 1.5 }),
        },
        {
            field: "message",
            headerName: "Message",
            ...(isMobile ? { width: 250 } : { flex: 5 }),
        },
        {
            field: "isRead",
            headerName: "IsRead",
            ...(isMobile ? { width: 100 } : { flex: 1.5 }),
        },
    ], [isMobile]);

    const handleDeleteClick = (id) => {
        setIdToDelete(id);
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };

    const handleBulkDelete = () => {
        const selectedIds = getSelectedIds(rowSelectionModel, notifications);
        if (selectedIds.length === 0) {
            toast.error('No rows selected for deletion!');
            return;
        }
        setDeleteType('bulk');
        setConfirmDialogOpen(true);
    };

    const executeDelete = useCallback(async () => {
        try {
            if (deleteType === 'single') {
                if (!idToDelete) return;
                await api.delete(`/notifications/delete/${idToDelete}`);
                setNotifications(notifications.filter((item) => item._id !== idToDelete));
                toast.success('Notification deleted successfully.');
            } else if (deleteType === 'bulk') {
                const selectedIds = getSelectedIds(rowSelectionModel, notifications);
                if (selectedIds.length === 0) {
                    toast.error('No rows selected for deletion!');
                    return;
                }
                const res = await api.patch('/notifications/soft-delete', { ids: selectedIds });
                toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
                setNotifications((prev) => prev.filter((item) => !selectedIds.includes(item._id)));
            }
        } catch (err) {
            toast.error("Failed to delete: " + err.message);
        }
    }, [deleteType, idToDelete, rowSelectionModel, notifications]);
    

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Notifications"
                    subtitle={loading ? 'Loading...' : 'List of Notifications'}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title={isSubscribed ? 'Unsubscribe' : 'Subscribe'} arrow>
                            <span>
                                <StyledButton
                                    color={isSubscribed ? 'error' : 'success'}
                                    disabled={loading || !vapidPublicKey}
                                    onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
                                >
                                    {isSubscribed ? <Unsubscribe /> : <Subscriptions />}
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Bulk delete" arrow>
                            <span>
                                <StyledButton
                                    disabled={!isAnyRowSelected(rowSelectionModel, notifications)}
                                    onClick={handleBulkDelete}
                                >
                                    <Delete />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Create" arrow>
                            <StyledButton onClick={() => navigate("/noticeboard")}>
                                <AddCircleOutline />
                            </StyledButton>
                        </Tooltip>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
                        {isMobile ? (
                            <CardList
                                rows={notifications}
                                primaryField="type"
                                fields={[
                                    { label: "Message", key: "message" },
                                    { label: "IsRead", key: "isRead" },
                                ]}
                                isActiveField={null}
                                canDelete={true}
                                onDelete={handleDeleteClick}
                            />
                        ) : (
                            <DataGridComponent
                                rows={notifications || []}
                                columns={columns}
                                columnVisibilityModel={columnVisibilityModel}
                                setColumnVisibilityModel={setColumnVisibilityModel}
                                loading={loading}
                                props={{
                                    checkboxSelection: true,
                                    disableRowSelectionOnClick: true,
                                    onRowSelectionModelChange: setRowSelectionModel,
                                    rowSelectionModel,
                                    initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}

            <DeleteConfirm
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={executeDelete}
                deleteType={deleteType}
            />
        </Box>
    );
};

export default Notifications;
